import React, { useEffect, useState, useReducer, useCallback, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import Demo from '../Demo'
import DemoInlineEditor from '../DemoInlineEditor'
import Expandable from '../Expandable'
import DemoPlayer from '../DemoPlayer'
import { useDrop } from 'react-dnd'
import { NativeTypes } from 'react-dnd-html5-backend'
import update from 'immutability-helper'
import Icon from 'components/Icon'
import { Button } from '@arnsbogroup/react-voicearchive'

const Demos = ({
  admin = false,
  supplier: { status = null, stage = null } = {},
  match: { params: { supplierId } },
  setCallback = () => {},
}) => {
  const { formatMessage } = useIntl()
  const fileRef = useRef()

  const [demos, setDemos] = useState([])
  const [audition, setAudition] = useState(null)
  const [studio, setStudio] = useState(null)
  const [droppedFiles, setDroppedFiles] = useState([])
  const [uploading, setUploading] = useState(null)

  useEffect(() => {
    setCallback((event) => new Promise((resolve, reject) => {
      if (stage !== 'ONBOARDED' && (!studio || !audition)) {
        event.stopPropagation()
        event.preventDefault()
        return reject(formatMessage(messages.validationFailed))
      }

      return resolve();
    }))

  }, [audition, studio, stage])

  useEffect(() => {
    fetch(`/rest/v2/supplier/${supplierId}/demos`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then(({ data }) => {
        let studio = null
        let audition = null
        let others = []

        data.forEach((demo) => {
          if (admin) {
            others.push(demo)
          } else if (!studio && demo.type === 'Studio') {
            studio = demo
          } else if (!audition && demo.type === 'Audition') {
            audition = demo
          } else {
            others.push(demo)
          }
        })

        setDemos(others.sort((a, b) => a.sorting - b.sorting ))
        setAudition(audition)
        setStudio(studio)
      })
      .catch((err) => {
        window.alert(err)
        console.error(err)
      })
  }, [supplierId])

  const upload = (type, audio) => {
    setUploading(type)

    const body = new FormData()
    body.append('addFileFile', audio)
    body.append('supplierid', supplierId)
    body.append('product_uid', 0)
    body.append('language_uid', 0)
    body.append('name', audio.name)
    body.append('filetype', { Studio: 3, Audition: 9 }[type])
    body.append('hide', true)

    fetch(`/supplier/addpromo.json`, { method: 'POST', body, credentials: 'same-origin' })
      .then((response) => response.json())
      .then(({ success, errorMsg, file }) => {
        if (!success) {
          window.alert(errorMsg)
        } else if (type === 'Studio') {
          setStudio({...file, type, cdn_url: URL.createObjectURL(audio) })
        } else if (type === 'Audition') {
          setAudition({...file, type, cdn_url: URL.createObjectURL(audio) })
        }

        setUploading(null)
      })
      .catch((err) => {
        window.alert(err)
        console.error(err)
      })
  }

  const onDelete = (demoId) => {
    if (audition && audition.uid === demoId) {
      setAudition(null)
    } else if (studio && studio.uid === demoId) {
      setStudio(null)
    } else {
      setDemos(demos.filter(({ uid }) => uid !== demoId))
    }

    fetch(`/supplier/removepromo.json?id=${demoId}`, { method: 'POST', credentials: 'same-origin' })
      .catch((err) => window.alert(err))
  }

  const onToggle = (prevDemo) => {
    fetch(`/supplierPromo/setVisibility.json?uid=${prevDemo.uid}&visible=${!prevDemo.active}`, { method: 'POST', credentials: 'same-origin' })
      .then((response) => response.json())
      .then(({ success, errors }) => {
        if (!success) {
          return window.alert(errors.join(', '))
        }

        setDemos((demos) => demos.map((demo) => {
          if (demo.uid == prevDemo.uid) {
            demo.active = !prevDemo.active
          }

          return demo
        }))
      })
      .catch((err) =>  window.alert(err))
  }

  const onMove = useCallback((dragIndex, hoverIndex) => {
    const dragDemo = demos[dragIndex]
    const nextDemos = update(demos, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragDemo],
      ],
    })

    setDemos(nextDemos)

    fetch(`/rest/v2/supplier/${supplierId}/demos`, {
      credentials: 'same-origin',
      method: 'PUT',
      body: JSON.stringify(nextDemos.map(({ uid }, sorting) => ({ uid, sorting })))
    }).catch((err) => {
      window.alert(err)
      console.error(err)
    })
  }, [demos])

  const onDrop = useCallback((item, monitor) => {
    if (monitor) {
      const files = monitor.getItem().files
      setDroppedFiles(files)
    }
  }, [])


  const [{ canDrop, isOver }, drop] = useDrop({
    accept: [NativeTypes.FILE],
    drop(item, monitor) {
      if (onDrop) {
        onDrop({}, monitor) // {} = props
      }
    },
    canDrop(item) {
      if (item.files.length > 1) {
        return false
      }
      return true
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  return (
    <div className="w-full">
      { !admin &&
        <React.Fragment>
          <div className="font-medium text-4xl">
            <FormattedMessage {...messages.title } />
          </div>

          <div className="text-gray-700 mt-6 w-2/3">
            <FormattedMessage {...messages.subtitle } />
          </div>

          { ['ONLINE', 'ACTIVE', 'ACCEPTED'].includes(status) &&
            <>
              <div className="text-gray-700 mt-6 w-2/3">
                <FormattedMessage {...messages.subtitleDialectsAccents } />
              </div>
              <div className="text-gray-700 mt-6 w-2/3 whitespace-pre-line">
                <FormattedMessage {...messages.subtitleExtra } />
              </div>
            </>
          }

          <Expandable
            className="mt-4"
            expandLabel={formatMessage(messages.specsExpandLabel)}
            collapseLabel={formatMessage(messages.specsCollapseLabel)}
          >
            <FormattedMessage {...messages.specsContent} />
          </Expandable>

        <div className="grid grid-cols-2 gap-20 my-12">
          <div className="flex flex-col justify-between">
            <p className="py-6">
              <FormattedMessage {...messages.studioTitle } />
              { stage !== 'ONBOARDED' ? (
                <span className="ml-1">*</span>
              ) : (
                <span className="ml-1 text-gray-500">(optional)</span>
              ) }
              { !admin && <div className="text-sm text-gray-700">
                <FormattedMessage {...messages.studioSubTitle } values={{
                  link: (chunks) => (
                    <a className="text-primary cursor-pointer" target="_blank" href="/docs/Demo%20Manuscript">
                      { chunks }
                    </a>
                  )
                }} />
              </div> }
            </p>
            { studio ? (
              <div className="flex items-center">
                <DemoPlayer src={studio.cdn_url} />
                <Button 
                  variant="inline"
                  state="negative"
                  size="sm"
                  onClick={() => onDelete(studio.uid)}
                >
                  Delete
                </Button>
              </div>
            ) : (
              <span>
                { uploading === 'Studio' ? (
                  <Icon icon="sync" className="animate-spin" />
                ) : (
                  <input type="file" onChange={({ target: { files } }) => upload('Studio', files[0])}/>
                ) }
              </span>
            ) }
          </div>
          <div className="flex flex-col justify-between">
            <p className="py-6">
              <FormattedMessage {...messages.auditionTitle } />
              { stage !== 'ONBOARDED' ? (
                <span className="ml-1">*</span>
              ) : (
                <span className="ml-1 text-gray-500">(optional)</span>
              ) }
              { !admin && <div className="text-sm text-gray-700" >
                <FormattedMessage {...messages.auditionSubTitle } />
              </div> }
            </p>
            { audition ? (
              <div className="flex items-center">
                <DemoPlayer src={audition.cdn_url} />
                <Button
                  variant="inline"
                  state="negative"
                  size="sm"
                  onClick={() => onDelete(audition.uid)}
                >
                  Delete
                </Button>
              </div>
            ) : (
              <span>
                { uploading === 'Audition' ? (
                  <Icon icon="sync" className="animate-spin" />
                ) : (
                  <input type="file" onChange={({ target: { files } }) => upload('Audition', files[0])}/>
                ) }
              </span>
            ) }
          </div>
        </div>

        { studio && audition && ['ONBOARDING', 'IN REVIEW'].includes(status) &&
          <div className="border border-gray-400 p-4 text-sm mt-12 text-gray-800 bg-gray-100 flex justify-between items-center">
            <Icon icon="check text-xl text-gray-400 ml-4 mr-8" />
            <FormattedMessage {...messages.continuePrompt} />
          </div>
        }

        </React.Fragment>
      }

      { ((studio && audition && !['ONBOARDING', 'IN REVIEW'].includes(status)) || admin || stage === 'ONBOARDED') &&
        <React.Fragment>
          <div className="py-6">
            <FormattedMessage {...messages.listTitle } /> *
            <div className="text-sm text-gray-700 w-full flex justify-between">
              <span>
                <FormattedMessage {...messages.listSubTitle } />
              </span>
              <a href={`/supplierPromo/archiveZip?supplierId=${supplierId}`} target="_blank" className="text-primary">
                <FormattedMessage {...messages.listDownloadAll } />
              </a>
            </div>
          </div>

          <div className="relative" ref={drop} onClick={(e) => e.stopPropagation()}>
            { canDrop && 
              <div className={`${isOver ? 'opacity-50' : canDrop ? 'opacity-25' : 'opacity-0'} transition-opacity absolute w-full h-full inset-0 z-50 bg-gray-300 shadow-inner flex justify-center items-center`}>
                <FormattedMessage {...messages.listDrop } />
              </div>
            }

            { !droppedFiles.length && !demos.length &&
              <div className={`w-full py-24 bg-gray-300 shadow-inner flex justify-center items-center ${canDrop ? 'text-transparent' : 'text-gray-600'}`}>
                <FormattedMessage {...messages.listDrop } />
              </div>
            }

            <div className="space-y-2">
              { demos.map((demo, i) => (
                <Demo
                  key={demo.uid}
                  demo={demo}
                  index={i}
                  onMove={onMove}
                  supplierId={supplierId}
                  onDelete={admin ? onDelete : demo.type || !!demo.supplierPromoPriorityIds?.length ? undefined : onDelete}
                  onToggle={admin ? onToggle : undefined}
                />
              )) }

              { droppedFiles.map((file, i) => (
                <DemoInlineEditor
                  onCancel={() => setDroppedFiles([])}
                  onAdded={(demo) => {
                    setDemos([...demos, demo])
                    setDroppedFiles([])
                  }}
                  supplierId={supplierId}
                  key={`edit-${i}`}
                  file={file}
                />
              )) }
            </div>

            <Button
              variant="inline"
              size="sm"
              className="float-right" 
              onClick={() => fileRef.current.click()}
              icon={['fa', 'plus']}
            >
              <FormattedMessage {...messages.fileUpload } />
            </Button>

            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={({ target }) => {
                if (!target.files.length) {
                  // User clicked "Cancel"
                  fileRef.current.value = ""
                  return
                }
                setDroppedFiles([target.files[0]]);
              }}
            />
          </div>
        </React.Fragment>
      }
    </div>
  )
}

export default Demos
